import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowDownToLine,
  ArrowRight,
  Boxes,
  Braces,
  Check,
  CheckCircle2,
  Code2,
  Database,
  FileCode2,
  FileJson,
  GitCompareArrows,
  GitFork,
  KeyRound,
  Layers3,
  Lock,
  Unlock,
  Save,
  List,
  Network,
  Plus,
  RotateCcw,
  Search,
  ShieldCheck,
  Sparkles,
  Table2,
  Upload,
  WandSparkles,
} from "lucide-react";
import type {
  Diagnostic,
  Dialect,
  Family,
  ModelDocument,
  ModelType,
  NamingRule,
  Operation,
  Preferences,
  Repository,
} from "../../shared/contracts";
import {
  blockDependencyClosure,
  buildingBlocks,
  createSubjectArea,
  documentOperations,
  extractModel,
  instantiateBlock,
  previewNaming,
  transformModel,
  allowedDerivationTargets,
  orderedBusinessKeys,
  logicalPerspective,
  uid,
} from "../../core";
import {
  compareModels,
  exportMongo,
  exportNative,
  generateDDL,
  generateSemanticSQL,
  importDDL,
  importMongo,
  importNative,
} from "../../engineering";
import { api, downloadBlob } from "../api";
import { dictionarySheets, exportXLSX, type DictionaryOptions } from '../../engineering/xlsx';
import { ddlExportRoots, selectDDLExport } from '../../engineering/exportSelection';
import ImportSourceFile from './ImportSourceFile';
import ModelTypeIcon from "./ModelTypeIcon";
import { defaultPreferences } from "../../shared/contracts";
import { displayDetailLevel, getThemeTokens } from "../../shared/appearance";
import { ContextHelp, CustomThemeEditor, PaletteChooser, RelationshipPreview, RelationshipStyleEditor, ThemeChooser, FontSizeInput, EntityDisplayControls } from "./AppearanceControls";
import { builtInWorkspaceLayout, captureWorkspacePreset, interfaceScaleForPreferences, normalizeInterfaceScale, toolbarLockAction } from "../../shared/workspace";
import { vocabulary } from "../../shared/entityPresentation";
import { preferenceLabels } from "./AppearancePolicyEditor";
import "./appearance-dialogs.css";
import { ExportBackgroundEditor } from "./ExportBackgroundEditor";
import { ModelTypePresentationEditor } from "./ModelTypePresentationEditor";
import {
  Button,
  Diagnostics,
  Field,
  LossSummary,
  Modal,
  Notice,
  dialectLabel,
  downloadText,
  errorText,
  modelTypeLabel,
} from "./Primitives";
export const dialects: Dialect[] = [
  "postgresql",
  "oracle",
  "sqlserver",
  "mysql",
  "ase",
];
export const modelTypes: ModelType[] = [
  "conceptual",
  "logical",
  "logical-physical",
  "physical",
];
export function NewModelDialog({
  repositories,
  initialRepository,
  onClose,
  onCreate,
  onCreateRepository,
}: {
  repositories: Repository[];
  initialRepository?: string;
  onClose: () => void;
  onCreateRepository?: () => void;
  onCreate: (data: any) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState<ModelType>("logical");
  const [family, setFamily] = useState<Family>("relational");
  const [dialect, setDialect] = useState<Dialect>("postgresql");
  const [repo, setRepo] = useState(
    initialRepository || repositories[0]?.repositoryId || "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await onCreate({
        name: name.trim(),
        modelType: type,
        family,
        ...(family === "relational" && ["physical", "logical-physical"].includes(type) ? {targetProfile: dialect} : {}),
        repositoryId: repo,
      });
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Create a model"
      subtitle="Start with an empty canvas. Add only what your design needs."
      onClose={onClose}
      wide
      footer={
        <>
          <span className="footer-note">No sample entities are added.</span>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            icon={Plus}
            variant="primary"
            busy={busy}
            disabled={!name.trim() || !repo}
            onClick={submit}
          >
            Create model
          </Button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      {!repositories.length && (
        <Notice kind="warning">
          A model needs a repository.{" "}
          {onCreateRepository ? (
            <>
              <br />
              <Button onClick={onCreateRepository}>
                Create your first repository
              </Button>
            </>
          ) : (
            "Ask your administrator to assign a repository."
          )}
        </Notice>
      )}
      <div className="form-row">
        <Field label="Model name">
          <input
            autoFocus
            placeholder="e.g. Customer operations"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Repository">
          <select value={repo} onChange={(e) => setRepo(e.target.value)}>
            {repositories.map((r) => (
              <option key={r.repositoryId} value={r.repositoryId}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Model family">
        <select
          value={family}
          onChange={(e) => setFamily(e.target.value as Family)}
        >
          <option value="relational">
            Relational · entities and relationships
          </option>
          <option value="semantic">
            Semantic · business definitions and grain
          </option>
          <option value="analytical">Analytical · facts and dimensions</option>
          <option value="document">
            Document · collections and nested fields
          </option>
          <option disabled>Graph · not available in this build</option>
          <option disabled>Process · not available in this build</option>
        </select>
      </Field>
      {family === "relational" ? (
        <>
          <div className="section-label">Where does your design begin?</div>
          <div className="model-type-grid">
            {modelTypes.map((t, i) => {
              
              return (
                <button
                  key={t}
                  className={`model-type-choice ${type === t ? "selected" : ""}`}
                  onClick={() => setType(t)}
                >
                  <ModelTypeIcon type={t} size={66} />
                  <strong>{modelTypeLabel(t)}</strong>
                  <span>
                    {
                      [
                        "Business concepts and how they relate.",
                        "Detailed attributes, rules, and business keys.",
                        "Logical meaning and target-ready structure.",
                        "Tables, columns, and database constraints.",
                      ][i]
                    }
                  </span>
                  {type === t && (
                    <CheckCircle2 size={17} className="choice-check" />
                  )}
                </button>
              );
            })}
          </div>
          {["physical", "logical-physical"].includes(type) && <Field
            label="Database target"
            hint="Used for physical design and SQL generation. Creating a model does not connect to a database."
          >
            <select
              value={dialect}
              onChange={(e) => setDialect(e.target.value as Dialect)}
            >
              {dialects.map((d) => (
                <option key={d} value={d}>
                  {dialectLabel(d)}
                </option>
              ))}
            </select>
          </Field>}
        </>
      ) : (
        <Notice>
          This opens a {family} design. Execution and runtime support depend on
          the declared adapter profile.
        </Notice>
      )}
    </Modal>
  );
}
export function ImportDialog({
  repositories,
  onClose,
  onImport,
}: {
  repositories: Repository[];
  onClose: () => void;
  onImport: (data: any) => Promise<void>;
}) {
  const [kind, setKind] = useState("ddl");
  const [text, setText] = useState("");
  const [dialect, setDialect] = useState<Dialect>("postgresql");
  const [repo, setRepo] = useState(repositories[0]?.repositoryId || "");
  const [name, setName] = useState("Imported model");
  const [result, setResult] = useState<any>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [ack, setAck] = useState(false);
  const inspect = () => {
    setError("");
    setResult(undefined);
    setAck(false);
    try {
      const r =
        kind === "ddl"
          ? importDDL(text, dialect)
          : kind === "native"
            ? importNative(text)
            : importMongo(text);
      setResult(r);
      if (r.document?.model?.name) setName(r.document.model.name);
    } catch (e) {
      setError(errorText(e));
    }
  };
  const apply = async () => {
    setBusy(true);
    try {
      await onImport({
        name,
        repositoryId: repo,
        modelType: result.document.model.modelType,
        family: result.document.model.family,
        targetProfile: dialect,
        initialDocument: result.document,
      });
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const hasErrors = result?.diagnostics?.some(
    (d: Diagnostic) => d.severity === "error",
  );
  const losses =
    (result?.lossReport?.lost?.length || 0) +
    (result?.lossReport?.unresolved?.length || 0);
  return (
    <Modal
      title="Import a model"
      subtitle="Inspect input and conversion details before creating a new model."
      onClose={onClose}
      wide
      footer={
        <>
          <span className="footer-note">
            Input is parsed as text. SQL is never executed.
          </span>
          <Button onClick={onClose}>Cancel</Button>
          {!result ? (
            <Button
              variant="primary"
              icon={Search}
              disabled={!text.trim()}
              onClick={inspect}
            >
              Inspect import
            </Button>
          ) : (
            <Button
              variant="primary"
              icon={Plus}
              busy={busy}
              disabled={
                hasErrors || !repo || !name.trim() || (!!losses && !ack)
              }
              onClick={apply}
            >
              Create imported model
            </Button>
          )}
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <div className="form-row">
        <Field label="Input format">
          <select
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setResult(undefined);
            }}
          >
            <option value="ddl">Database DDL</option>
            <option value="native">DataGlow model JSON</option>
            <option value="mongo">MongoDB JSON schema</option>
          </select>
        </Field>
        {kind === "ddl" && (
          <Field label="Source dialect">
            <select
              value={dialect}
              onChange={(e) => {
                setDialect(e.target.value as Dialect);
                setResult(undefined);
              }}
            >
              {dialects.map((d) => (
                <option key={d} value={d}>
                  {dialectLabel(d)}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      {!result ? (
        <>
          <ImportSourceFile onDecoded={source => { setText(source); setResult(undefined); setError(''); }} onError={message => { setText(''); setResult(undefined); setError(message); }}/>
          <Field label={kind === "ddl" ? "SQL source" : "Model source"}>
            <textarea
              className="code-input"
              rows={12}
              value={text}
              onChange={(e) => setText(e.target.value)}
              spellCheck={false}
              placeholder={
                kind === "ddl"
                  ? "Paste CREATE TABLE statements here…"
                  : "Paste JSON here…"
              }
            />
          </Field>
          <Notice>
            Native erwin and ER/Studio binaries are not verified in this build.
            Use only a declared, supported interchange route.
          </Notice>
        </>
      ) : (
        <>
          <div className="import-result-bar">
            <CheckCircle2 size={20} />
            <div>
              <strong>Import inspected</strong>
              <span>
                {result.document?.objects?.filter((o: any) => !o.ownerObjectId)
                  .length || 0}{" "}
                root objects · {result.document?.relationships?.length || 0}{" "}
                relationships
              </span>
            </div>
            <Button onClick={() => setResult(undefined)}>Edit input</Button>
          </div>
          <div className="form-row">
            <Field label="New model name">
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Repository">
              <select value={repo} onChange={(e) => setRepo(e.target.value)}>
                {repositories.map((r) => (
                  <option key={r.repositoryId} value={r.repositoryId}>
                    {r.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Diagnostics items={result.diagnostics || []} />
          <LossSummary loss={result.lossReport} />
          {!!losses && (
            <label className="check-row">
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
              />
              I reviewed the loss and unresolved-property report.
            </label>
          )}
        </>
      )}
    </Modal>
  );
}
export function ExportDialog({
  document,
  diagramId,
  selectedIds,
  revision,
  onClose,
}: {
  document: ModelDocument;
  diagramId?: string;
  selectedIds?: readonly string[];
  revision?: number;
  onClose: () => void;
}) {
  const [format, setFormat] = useState("native");
  const [ddlSelection, setDdlSelection] = useState<string[]>();
  const [ddlAcknowledged, setDdlAcknowledged] = useState(false);
  const ddlRoots = useMemo(() => ddlExportRoots(document), [document]);
  const ddlSubset = useMemo(() => ddlSelection ? selectDDLExport(document, ddlSelection) : undefined, [document, ddlSelection]);
  useEffect(() => { setDdlAcknowledged(false); }, [document]);
  const [scope, setScope] = useState<DictionaryOptions['scope']>('model');
  const [exportedAt] = useState(() => new Date().toISOString());
  const activeDiagram = document.diagrams.find(diagram => diagram.diagramId === diagramId);
  const dictionaryOptions = useMemo(() => ({ scope, diagramId, selectedIds, revision, exportedAt, representationId: activeDiagram?.representationId }), [scope, diagramId, selectedIds, revision, exportedAt, activeDiagram?.representationId]);
  const dictionary = useMemo(() => format === 'xlsx' ? dictionarySheets(document, dictionaryOptions) : undefined, [format, document, dictionaryOptions]);
  const [dialect, setDialect] = useState<Dialect>(
    document.model.targetProfile || "postgresql",
  );
  const result = useMemo(() => {
    try {
      if (format === 'xlsx') return { text: '', bytes: exportXLSX(document, dictionaryOptions), diagnostics: [], extension: 'xlsx' };
      if (format === "native")
        return {
          text: exportNative(document),
          diagnostics: [],
          extension: "dataglow.json",
        };
      if (format === "ddl") {
        const r = generateDDL(ddlSubset?.document ?? document, dialect);
        if (ddlSubset) {
          r.lossReport.lost.push(...ddlSubset.omittedRoots.map(object => `${object.name}: excluded from this export`), ...ddlSubset.omittedRelationships.map(relationship => `${relationship.name}: excluded because an endpoint is outside this export`));
          if (!ddlSubset.document.objects.length) r.diagnostics.push({ code: 'DDL_EMPTY_SELECTION', severity: 'error', message: 'Select at least one supported object for export.' });
        }
        return { ...r, text: r.sql, extension: "sql" };
      }
      if (format === "mongo") {
        const r = exportMongo(document);
        return { ...r, text: r.json, extension: "schema.json" };
      }
      const r = generateSemanticSQL(document);
      return { ...r, text: r.sql, extension: "sql" };
    } catch (e) {
      return {
        text: "",
        extension: "txt",
        diagnostics: [
          { code: "EXPORT_ERROR", severity: "error", message: errorText(e) },
        ],
      };
    }
  }, [format, dialect, document, dictionaryOptions, ddlSubset]);
  const blocked = result.diagnostics?.some((d: any) => d.severity === "error") || (format === 'ddl' && !!ddlSubset?.omittedRoots.length && !ddlAcknowledged);
  return (
    <Modal
      title="Export model"
      subtitle="Choose a portable artifact or inspect generated database text."
      wide
      onClose={onClose}
      footer={
        <>
          <span className="footer-note">
            Export never changes a source database.
          </span>
          <Button onClick={onClose}>Close</Button>
          <Button
            icon={ArrowDownToLine}
            variant="primary"
            disabled={blocked || (!result.text && !('bytes' in result && result.bytes))}
            onClick={() => 'bytes' in result && result.bytes ? downloadBlob(`${document.model.name.replace(/[^a-z0-9_-]/gi, '_')}.xlsx`, new Blob([new Uint8Array(result.bytes)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })) :
              downloadText(
                `${document.model.name.replace(/[^a-z0-9_-]/gi, "_")}.${result.extension}`,
                result.text,
                format === "native" ? "application/json" : "text/plain",
              )
            }
          >
            Download {format === "native" ? "model" : format === 'xlsx' ? 'XLSX' : "output"}
          </Button>
        </>
      }
    >
      <div className="form-row">
        <Field label="Export format">
          <select value={format} onChange={(e) => setFormat(e.target.value)}>
            <option value="native">
              DataGlow model JSON · full-fidelity package
            </option>
            <option value="xlsx">Excel data dictionary (.xlsx)</option>
            {document.model.family === "relational" && (
              <option value="ddl">Database DDL</option>
            )}
            {document.model.family === "document" && (
              <option value="mongo">MongoDB schema</option>
            )}
            {["semantic", "analytical"].includes(document.model.family) && (
              <option value="semantic">Semantic SQL</option>
            )}
          </select>
        </Field>
        {format === "ddl" && (
          <Field label="Target dialect">
            <select
              value={dialect}
              onChange={(e) => { setDialect(e.target.value as Dialect); setDdlAcknowledged(false); }}
            >
              {dialects.map((d) => (
                <option key={d} value={d}>
                  {dialectLabel(d)}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      {format === 'ddl' && !!ddlRoots.length && <details open={!!ddlSubset || result.diagnostics.some(d => d.severity === 'error')}><summary>Choose objects for DDL export ({ddlSelection?.length ?? ddlRoots.length} of {ddlRoots.length})</summary>
        <p>Exclude objects with unresolved generation errors to review a valid subset. Selected objects keep all of their fields, keys and indexes. Dependencies are revalidated after every change.</p>
        {ddlRoots.map(object => <label className="check-row" key={object.objectId}><input type="checkbox" aria-label={`Export ${object.schema ? object.schema + '.' : ''}${object.name}`} checked={ddlSelection ? ddlSelection.includes(object.objectId) : true} onChange={event => {
          const ids = ddlSelection ?? ddlRoots.map(root => root.objectId);
          setDdlSelection(event.target.checked ? [...ids, object.objectId] : ids.filter(id => id !== object.objectId)); setDdlAcknowledged(false);
        }}/>{object.schema ? object.schema + '.' : ''}{object.name} <small>{object.kind}</small></label>)}
        {!!ddlSubset?.omittedRoots.length && <><Notice>{ddlSubset.omittedRoots.length} objects and their descendants omitted. {ddlSubset.omittedRelationships.length} relationships omitted because an endpoint is outside the selected subset. Unsupported dependencies in the remaining selection still block download.</Notice><label className="check-row"><input type="checkbox" checked={ddlAcknowledged} onChange={event => setDdlAcknowledged(event.target.checked)}/>I reviewed the selected DDL objects, omitted relationships and dependency report.</label></>}
      </details>}
      {format === 'xlsx' && <><Field label="Dictionary scope"><select aria-label="Dictionary scope" value={scope} onChange={e => setScope(e.target.value as DictionaryOptions['scope'])}>
        <option value="model">Whole model · all representations</option><option value="representation">Active representation</option>
        {activeDiagram && <option value="diagram">{activeDiagram.role === 'subject-area' ? 'Subject area' : activeDiagram.role === 'view' ? 'View' : 'Active diagram'} · {activeDiagram.name}</option>}
        {!!selectedIds?.length && <option value="selection">Selected objects · full metadata</option>}
      </select></Field><Notice>{dictionary?.summary}. Includes every attribute in scope, independent of canvas display limits.</Notice>
      <table className="dictionary-sheet-preview"><thead><tr><th>Worksheet</th><th>Data rows</th></tr></thead><tbody>{dictionary?.sheets.map(sheet => <tr key={sheet.name}><td>{sheet.name}</td><td>{sheet.rows.length - 1}</td></tr>)}</tbody></table></>}
      <Diagnostics items={(result.diagnostics as Diagnostic[]) || []} />
      <LossSummary
        loss={"lossReport" in result ? result.lossReport : undefined}
      />
      {format !== 'xlsx' && <textarea
        className="code-input export-code"
        readOnly
        value={result.text}
        rows={16}
        aria-label="Generated export"
        spellCheck={false}
      />}
      <Notice>
        {format === 'xlsx' ? 'Editable Excel workbook with filters and frozen headers. User-authored text is stored as literal cell content. Constraints and notes distinguishes modeled metadata from preserved source evidence.' : 'Support is specific to the format, operation, and target profile. Generated SQL must be reviewed before use in a database.'}
      </Notice>
    </Modal>
  );
}
export function BlocksDialog({
  document,
  onClose,
  onOperations,
}: {
  document: ModelDocument;
  onClose: () => void;
  onOperations: (ops: Operation[], summary: string) => void;
}) {
  const [selected, setSelected] = useState(buildingBlocks[0]?.id || "");
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("all");
  const [industry, setIndustry] = useState("all");
  const industries = [...new Set(buildingBlocks.map(block => block.industry).filter(Boolean))].sort() as string[];
  const filteredBlocks = buildingBlocks.filter(block => (group === "all" || block.group === group) && (industry === "all" || block.industry === industry) && `${block.id} ${block.name} ${block.description} ${block.category} ${block.industry || ""} ${(block.tags || []).join(" ")}`.toLowerCase().includes(search.trim().toLowerCase()));
  const [prefix, setPrefix] = useState("");
  const [decisions, setDecisions] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<any>();
  const [error, setError] = useState("");
  useEffect(() => {
    if (!filteredBlocks.some(block => block.id === selected)) {
      setSelected(filteredBlocks[0]?.id || ''); setPreview(undefined); setDecisions({}); setError('');
    }
  }, [group, industry, search, selected]);
  const block = buildingBlocks.find((b) => b.id === selected);
  const dependencies = selected
    ? blockDependencyClosure(selected).filter((id) => id !== selected)
    : [];
  const inspect = () => {
    try {
      setPreview(
        instantiateBlock(document, selected, {
          prefix,
          dependencies: Object.fromEntries(
            Object.entries(decisions).map(([id, action]) => [id, { action }]),
          ),
        } as any),
      );
      setError("");
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Modal
      title="Building blocks"
      subtitle={`${buildingBlocks.length} reusable reference models. Choose an industry, corporate function or shared foundation.`}
      wide
      onClose={onClose}
      footer={
        <>
          <span className="footer-note">
            Blocks create new identities; names alone never merge objects.
          </span>
          <Button onClick={onClose}>Cancel</Button>
          {preview ? (
            <Button
              variant="primary"
              disabled={
                !preview.operations?.length ||
                preview.diagnostics?.some(
                  (d: Diagnostic) => d.severity === "error",
                )
              }
              onClick={() => {
                onOperations(preview.operations, `Insert ${block?.name} block`);
                onClose();
              }}
            >
              Insert into draft
            </Button>
          ) : (
            <Button
              variant="primary"
              icon={Search}
              disabled={!block || dependencies.some((d) => !decisions[d])}
              onClick={inspect}
            >
              Preview insertion
            </Button>
          )}
        </>
      }
    >
      <div className="form-row"><Field label="Block group"><select aria-label="Block group" value={group} onChange={e => {setGroup(e.target.value);if(e.target.value !== 'industry')setIndustry('all');}}><option value="all">All groups</option><option value="industry">Industry domains</option><option value="corporate">Corporate functions</option><option value="foundation">Shared foundations</option></select></Field>
        <Field label="Industry"><select aria-label="Industry" value={industry} disabled={group !== 'all' && group !== 'industry'} onChange={e => setIndustry(e.target.value)}><option value="all">All industries</option>{industries.map(name => <option key={name} value={name}>{name}</option>)}</select></Field></div>
      <div className="block-browser">
        <div className="block-list">
          <div className="search-input">
            <Search size={14} />
            <input
              placeholder="Find a building block"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <small className="subtle">{filteredBlocks.length} matching reference models</small>
          {!filteredBlocks.length && <p>No blocks match these filters.</p>}
          {filteredBlocks            .map((b) => (
              <button
                className={`block-choice ${selected === b.id ? "active" : ""}`}
                key={b.id}
                onClick={() => {
                  setSelected(b.id);
                  setPreview(undefined);
                  setDecisions({});
                }}
              >
                <Boxes size={18} />
                <span>
                  <strong>{b.name}</strong>
                  <small>
                    {b.industry || b.category} · {b.entityCount} entities
                  </small>
                </span>
                <ArrowRight size={14} />
              </button>
            ))}
        </div>
        <div className="block-detail">
          {block && (
            <>
              <div className="block-detail-icon">
                <Boxes size={30} />
              </div>
              <span className="eyebrow">REUSABLE MODEL · v{block.version}</span>
              <h3>{block.name}</h3>
              <p>{block.description}</p>
              {!!block.assumptions?.length && <details><summary>Scope and assumptions</summary><ul>{block.assumptions.map((text,index)=><li key={index}>{text}</li>)}</ul></details>}
              {!!block.sourceReferences?.length && <details><summary>Reference sources</summary><ul>{block.sourceReferences.map(reference=><li key={reference.url}><a href={reference.url} target="_blank" rel="noreferrer">{reference.title}</a></li>)}</ul><small>These are DataGlow reference models for review, not certified industry standards.</small></details>}
              <Field label="Optional name prefix">
                <input
                  placeholder="e.g. billing_"
                  value={prefix}
                  onChange={(e) => {
                    setPrefix(e.target.value);
                    setPreview(undefined);
                  }}
                />
              </Field>
              {dependencies.length > 0 && (
                <>
                  <div className="section-label">Dependency decisions</div>
                  {dependencies.map((dep) => (
                    <Field
                      key={dep}
                      label={
                        buildingBlocks.find((b) => b.id === dep)?.name || dep
                      }
                    >
                      <select
                        value={decisions[dep] || ""}
                        onChange={(e) => {
                          setDecisions({ ...decisions, [dep]: e.target.value });
                          setPreview(undefined);
                        }}
                      >
                        <option value="">Choose explicitly…</option>
                        <option value="create">
                          Create a separate dependency instance
                        </option>
                      </select>
                    </Field>
                  ))}
                  <p className="subtle">
                    Reuse requires an explicit existing-instance mapping. This
                    insertion creates the dependencies you select.
                  </p>
                </>
              )}
              {error && <Notice kind="error">{error}</Notice>}
              {preview && (
                <>
                  <Notice>
                    {preview.operations.length} canonical operations prepared.
                    Review diagnostics before insertion.
                  </Notice>
                  <Diagnostics items={preview.diagnostics || []} />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
export function TransformDialog({
  document,
  sourceRepresentationId,
  preferences = defaultPreferences,
  onClose,
  onOperations,
}: {
  document: ModelDocument;
  sourceRepresentationId?: string;
  preferences?: Preferences;
  onClose: () => void;
  onOperations: (ops: Operation[], summary: string) => void | boolean;
}) {
  const targets = allowedDerivationTargets(document, sourceRepresentationId);
  const [target, setTarget] = useState<ModelType>(targets[0] ?? "logical");
  const [logicalPolicy, setLogicalPolicy] = useState(preferences.logicalPrimaryKeyPolicy ?? "business-first");
  const [removeSurrogates, setRemoveSurrogates] = useState(!!preferences.removeSurrogatesOnDerive);
  const [primaryKeys, setPrimaryKeys] = useState<Record<string,string>>({});
  const [confirmedSurrogateIds, setConfirmedSurrogateIds] = useState<string[]>([]);
  const [dialect, setDialect] = useState<Dialect>(
    document.model.targetProfile || "postgresql",
  );
  const sourcePhysical = document.representations.find(r=>r.representationId === (sourceRepresentationId || document.model.activeRepresentation))?.kind === "physical";
  const physicalTarget = target === "physical" || (target === "logical-physical" && !sourcePhysical);
  const [strategy, setStrategy] = useState("business-primary");
  const [result, setResult] = useState<any>();
  const [error, setError] = useState("");
  const [fieldTypes, setFieldTypes] = useState<Record<string, any>>({});
  const fields = document.objects.filter(
    (o) =>
      ["attribute", "column"].includes(o.kind) &&
      o.representationId ===
        (sourceRepresentationId || document.model.activeRepresentation),
  );
  const preview = () => {
    setError("");
    try {
      setResult(
        transformModel(document, target, dialect, {
          ...(physicalTarget ? { ...(target === "physical" ? { keyStrategy: strategy } : {}), fieldTypes } : {}),
          sourceRepresentationId,
          logicalPrimaryKeyPolicy: logicalPolicy,
          removeSurrogates: target === "logical" && removeSurrogates,
          primaryKeys,
          confirmedSurrogateIds,
        } as any),
      );
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Modal
      title="Derive a representation"
      subtitle="Preserve business meaning while choosing a target design."
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {result ? (
            <Button
              variant="primary"
              disabled={result.diagnostics?.some(
                (d: Diagnostic) => d.severity === "error",
              )}
              onClick={() => {
                const accepted = onOperations(
                  documentOperations(document, result.document),
                  `Derive ${modelTypeLabel(target)} representation`,
                );
                if (accepted !== false) onClose();
              }}
            >
              Apply reviewed derivation
            </Button>
          ) : (
            <Button icon={GitFork} variant="primary" onClick={preview}>
              Preview derivation
            </Button>
          )}
        </>
      }
    >
      <Notice>
        The original conceptual baseline stays distinct from generated views.
        Unresolved key and type decisions must be reviewed.
      </Notice>
      <div className="form-row">
        <Field label="Target model type">
          <select
            value={target}
            onChange={(e) => {
              setTarget(e.target.value as ModelType);
              setResult(undefined);
            }}
          >
            {targets.map((t) => (
              <option key={t} value={t}>
                {modelTypeLabel(t)}
              </option>
            ))}
          </select>
        </Field>
        {physicalTarget && <Field label="Database target">
          <select
            value={dialect}
            onChange={(e) => {
              setDialect(e.target.value as Dialect);
              setResult(undefined);
            }}
          >
            {dialects.map((d) => (
              <option key={d} value={d}>
                {dialectLabel(d)}
              </option>
            ))}
          </select>
        </Field>}
      </div>
      {(target === "logical" || target === "conceptual") && <>
        {target === "logical" && <><Field label="Logical primary-key policy"><select value={logicalPolicy} onChange={e=>{setLogicalPolicy(e.target.value as typeof logicalPolicy);setPrimaryKeys({});setResult(undefined);}}><option value="business-first">Promote first ordered business key</option><option value="retain-source">Retain source primary key</option></select></Field>
        <label className="check-row"><input type="checkbox" checked={removeSurrogates} onChange={e=>{setRemoveSurrogates(e.target.checked);setResult(undefined);}}/>Remove surrogate keys from derived logical model</label><p className="subtle">Removal is independent of PK promotion. Explicit surrogate metadata or your confirmation is required. Dependent fields stay in the proposal until valid business mappings are supplied.</p>
        {removeSurrogates && <details><summary>Confirm additional technical surrogate attributes</summary>{fields.map(field=><label key={field.objectId} className="check-row"><input type="checkbox" checked={confirmedSurrogateIds.includes(field.objectId)} onChange={e=>{setConfirmedSurrogateIds(ids=>e.target.checked ? [...ids,field.objectId] : ids.filter(id=>id!==field.objectId));setResult(undefined);}}/>{document.objects.find(o=>o.objectId===field.ownerObjectId)?.name}.{field.name}</label>)}</details>}</>}
        {(target === "conceptual" || logicalPolicy === "business-first") && document.objects.filter(o=>!o.ownerObjectId && o.representationId === (target === "conceptual" && document.model.modelType === "logical-physical" ? logicalPerspective(document,sourceRepresentationId)?.representationId : sourceRepresentationId || document.model.activeRepresentation)).map(owner=>{const groups=orderedBusinessKeys(document.objects.filter(o=>o.ownerObjectId===owner.objectId));return groups.length > 0 ? <Field key={owner.objectId} label={`${owner.name} business identifier`}><select value={primaryKeys[owner.objectId] ?? ""} onChange={e=>{setPrimaryKeys({...primaryKeys,[owner.objectId]:e.target.value});setResult(undefined);}}><option value="">First ordered group: {groups[0]!.name}</option>{groups.map((key,i)=><option key={key.objectId} value={key.objectId}>{i+1}. {key.name}: {key.members?.map(id=>document.objects.find(o=>o.objectId===id)?.name).join(' + ')}</option>)}</select></Field> : null;})}
      </>}
      {target === "logical-physical" && <Notice>The combined model retains source primary identifiers and business keys. Conceptual derivation uses its logical perspective.</Notice>}
      {physicalTarget && <>{target === "physical" && <Field label="Physical primary-key policy">
        <select
          value={strategy}
          onChange={(e) => {
            setStrategy(e.target.value);
            setResult(undefined);
          }}
        >
          <option value="business-primary">
            Use the selected business key
          </option>
          <option value="surrogate-primary">
            Generate a surrogate; preserve business uniqueness
          </option>
        </select>
      </Field>}
      <details className="details-card">
        <summary>
          Review field-type decisions <span>{fields.length} fields</span>
        </summary>
        {fields.map((f) => (
          <Field
            key={f.objectId}
            label={`${document.objects.find((o) => o.objectId === f.ownerObjectId)?.name || ""}.${f.name}`}
          >
            <input
              placeholder={f.type?.name || "Choose a concrete type"}
              value={fieldTypes[f.objectId]?.name || ""}
              onChange={(e) => {
                setFieldTypes({
                  ...fieldTypes,
                  [f.objectId]: { name: e.target.value },
                });
                setResult(undefined);
              }}
            />
          </Field>
        ))}
      </details></>}
      {error && <Notice kind="error">{error}</Notice>}
      {result && (
        <>
          <Diagnostics items={result.diagnostics || []} />
          <LossSummary loss={result.lossReport} />
          <div className="preview-counts">
            <span>
              {result.document.representations.length} representations
            </span>
            <span>{result.document.mappings.length} traceable mappings</span>
            <span>
              {documentOperations(document, result.document).length} proposed
              operations
            </span>
          </div>
        </>
      )}
    </Modal>
  );
}
export function NamingDialog({
  document,
  onClose,
  onOperations,
}: {
  document: ModelDocument;
  onClose: () => void;
  onOperations: (ops: Operation[], summary: string) => void;
}) {
  const [rule, setRule] = useState<NamingRule>({
    ruleSetId: uid(),
    name: "Workspace naming rule",
    version: 1,
    case: "snake_case",
    tablePrefix: "",
    columnPrefix: "",
    surrogateSuffix: "_id",
    businessSuffix: "_code",
    abbreviations: {},
    maxLength: 63,
  });
  const [preview, setPreview] = useState<any>();
  const [error, setError] = useState("");
  const update = (c: Partial<NamingRule>) => {
    setRule({ ...rule, ...c });
    setPreview(undefined);
  };
  return (
    <Modal
      title="Naming policy"
      subtitle="Preview explicit renames. Unrelated edits will never rename your model."
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {preview ? (
            <Button
              variant="primary"
              disabled={
                !preview.operations.length ||
                preview.diagnostics.some(
                  (d: Diagnostic) => d.severity === "error",
                )
              }
              onClick={() => {
                onOperations(preview.operations, `Apply ${rule.name}`);
                onClose();
              }}
            >
              Apply {preview.operations.length} changes
            </Button>
          ) : (
            <Button
              variant="primary"
              icon={WandSparkles}
              onClick={() => {
                try {
                  setPreview(previewNaming(document, rule));
                  setError("");
                } catch (e) {
                  setError(errorText(e));
                }
              }}
            >
              Preview renames
            </Button>
          )}
        </>
      }
    >
      <Field label="Rule name">
        <input
          value={rule.name}
          onChange={(e) => update({ name: e.target.value })}
        />
      </Field>
      <div className="form-row">
        <Field label="Case convention">
          <select
            value={rule.case}
            onChange={(e) => update({ case: e.target.value as any })}
          >
            {[
              "preserve",
              "snake_case",
              "camelCase",
              "PascalCase",
              "UPPER_SNAKE",
              "Title Case",
            ].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </Field>
        <Field label="Maximum identifier length">
          <input
            type="number"
            min={1}
            max={256}
            value={rule.maxLength}
            onChange={(e) => update({ maxLength: Number(e.target.value) })}
          />
        </Field>
      </div>
      <div className="form-row">
        <Field label="Table prefix">
          <input
            value={rule.tablePrefix}
            onChange={(e) => update({ tablePrefix: e.target.value })}
          />
        </Field>
        <Field label="Column prefix">
          <input
            value={rule.columnPrefix}
            onChange={(e) => update({ columnPrefix: e.target.value })}
          />
        </Field>
      </div>
      <div className="form-row">
        <Field label="Surrogate-key suffix">
          <input
            value={rule.surrogateSuffix}
            onChange={(e) => update({ surrogateSuffix: e.target.value })}
          />
        </Field>
        <Field label="Business-key suffix">
          <input
            value={rule.businessSuffix}
            onChange={(e) => update({ businessSuffix: e.target.value })}
          />
        </Field>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {preview && (
        <>
          <Diagnostics items={preview.diagnostics} />
          <div className="rename-table">
            {preview.operations.map((op: Operation, i: number) => (
              <div key={i}>
                <span>
                  {document.objects.find((o) => o.objectId === op.objectId)
                    ?.name || op.objectId}
                </span>
                <ArrowRight size={13} />
                <strong>{String(op.changes?.name || "Policy change")}</strong>
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}
function AutosaveIntervalInput({value, onChange}: {value:number; onChange:(value:number)=>void}) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  return <Field label="Model autosave interval (seconds)" hint="0 saves completed changes after a short pause. 15 saves pending changes every 15 seconds, even while you keep editing."><input type="number" min={0} max={86400} step="any" value={text} onChange={event => {
    const raw = event.target.value; setText(raw);
    const number = Number(raw);
    if (raw.trim() && Number.isFinite(number) && number >= 0 && number <= 86400) onChange(number);
  }} onBlur={() => { if (!text.trim() || !Number.isFinite(Number(text)) || Number(text) < 0 || Number(text) > 86400) setText(String(value)); }}/></Field>;
}
export function PreferencesDialog({
  preferences, onClose, onChange, onFlush, saveState, saveError, onHelp, document, diagramId,
}: {
  preferences: Preferences;
  document?: ModelDocument;
  diagramId?: string;
  onClose: () => void;
  onChange: (changes: Partial<Preferences>) => void;
  onFlush: () => Promise<void>;
  saveState: "idle" | "pending" | "saving" | "saved" | "error";
  saveError: string;
  onHelp?: (topic: string) => void;
}) {
  const draft = preferences;
  const [locked, setLocked] = useState<string[]>([]);
  const [policyState, setPolicyState] = useState<"loading" | "ready" | "unavailable">("loading");
  useEffect(() => {
    let active = true;
    api("/administration/appearance-policy").then(policy => {
      if (active) { setLocked(policy.locked || []); setPolicyState("ready"); }
    }).catch(() => { if (active) setPolicyState("unavailable"); });
    return () => { active = false; };
  }, []);
  const update = (c: Partial<Preferences>) => {
    const allowed = Object.fromEntries(Object.entries(c).filter(([key]) => !locked.includes(key)));
    if (Object.keys(allowed).length) onChange(allowed);
  };
  const isLocked = (key: keyof Preferences) => locked.includes(key);
  const close = () => { void onFlush().catch(() => {}); onClose(); };
  const tokens = getThemeTokens(draft);
  const diagram = document?.diagrams.find(item => item.diagramId === diagramId);
  const words = document ? vocabulary(document, diagram?.representationId, undefined, diagram?.perspective) : undefined;
  const conceptual = !!words?.conceptual;
  const capital = (word: string) => word[0].toUpperCase() + word.slice(1);
  const titleLabel = words ? `${capital(words.object)} name size` : "Entity title size";
  const fieldLabel = words ? `${capital(words.field)} text size` : "Field size";
  const detailOverrideCount = new Set([...Object.keys(draft.diagramDetailLevels || {}), ...Object.keys(draft.diagramEntityDisplay || {})]).size;
  const autoAdjust = draft.autoAdjustNearbyEntities !== false || draft.autoAdjustRelationships !== false;
  return <Modal title="Workspace preferences" subtitle="Valid changes save automatically after a short pause. Slider changes save when you finish dragging." onClose={close} wide footer={<>
    <ContextHelp topic="preferences" onHelp={onHelp} /><span className="a210-footer-spacer" />
    <span role="status" className="preference-save-state">{saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : saveState === "pending" ? "Unsaved changes" : saveState === "error" ? "Preferences not saved" : "Changes save automatically"}</span>
    {saveError && <Button onClick={() => void onFlush().catch(() => {})}>Retry</Button>}
    <Button variant="primary" onClick={close}>Close</Button>
  </>}>
    {saveError && <Notice kind="error">{saveError} Your changes remain available; retry to save.</Notice>}
    {!!locked.length && <Notice>Managed by your studio: {locked.map(key => preferenceLabels[key] || key).join(", ")}. These values are locked; your other preferences remain personal.</Notice>}
    {policyState === "unavailable" && <Notice>Studio policy could not be loaded. The server will check your permissions when you save.</Notice>}
    <fieldset className="a210-preferences">
      <section className="a210-preference-section">
        <div className="a210-section-heading"><div><h3><Sparkles size={18} />Choose your theme</h3><p>Coordinated colors for the canvas, text, selection, and relationships.</p></div><span className="a210-preview-badge">Live preview</span></div>
        <ThemeChooser value={draft} onChange={update} disabled={isLocked("theme")} />
        {draft.theme === "custom" && <CustomThemeEditor value={draft.customTheme} disabled={isLocked("customTheme")} onChange={customTheme => update({ customTheme })} />}
        <div className="form-row a210-spaced-row"><Field label="Accent" hint="Used by presets that support an accent color."><div className="a210-color-field"><input aria-label="Accent" type="color" disabled={isLocked("accent")} value={draft.accent} onChange={e => update({ accent: e.target.value })} /><code>{draft.accent}</code></div></Field><Field label="Density"><select disabled={isLocked("density")} value={draft.density} onChange={e => update({ density: e.target.value as any })}><option value="comfortable">Comfortable</option><option value="compact">Compact</option></select></Field></div>
      </section>
      <div className="a210-preferences-columns">
        <section className="a210-preference-section">
          <div className="a210-section-heading"><div><h3><Table2 size={18} />Entity settings</h3><p>Personal display defaults for entities and tables. Diagram-specific detail choices take precedence.</p></div></div>
          <fieldset><legend>New empty entities and derivation</legend>
            <label className="check-row"><input type="checkbox" checked={draft.autoAddId !== false} onChange={e => update({autoAddId:e.target.checked})}/>Automatically add ID</label>
            <label className="check-row"><input type="checkbox" checked={draft.autoAddDescription !== false} onChange={e => update({autoAddDescription:e.target.checked})}/>Automatically add description</label>
            <p className="subtle">Applies only to new empty entities. Both fields are mandatory; ID is the primary identifier. Imports and derived models retain their own fields.</p>
            <Field label="Nullability wording"><select value={draft.nullabilityWording ?? "null"} onChange={e => update({nullabilityWording:e.target.value as Preferences["nullabilityWording"]})}><option value="null">NULL / NOT NULL</option><option value="mandatory">Mandatory: Yes / No</option></select></Field>
            <Field label="Derived logical primary key"><select value={draft.logicalPrimaryKeyPolicy ?? "business-first"} onChange={e => update({logicalPrimaryKeyPolicy:e.target.value as Preferences["logicalPrimaryKeyPolicy"]})}><option value="business-first">First ordered business key</option><option value="retain-source">Retain source primary key</option></select></Field>
            <label className="check-row"><input type="checkbox" checked={!!draft.removeSurrogatesOnDerive} onChange={e => update({removeSurrogatesOnDerive:e.target.checked})}/>Remove confirmed surrogates in logical derivation (review dependencies)</label>
          </fieldset>
          <Field label="Default entity palette"><PaletteChooser value={draft.paletteId} disabled={isLocked("paletteId")} onChange={paletteId => paletteId && update({ paletteId })} /></Field>
          <EntityDisplayControls value={draft.entityDisplay} level={draft.detailLevel} showDescription={draft.showEntityDescription} conceptual={conceptual} physical={!!words?.physical} lockedKeys={locked} label="Default entity details" onChange={entityDisplay => update({entityDisplay, detailLevel: displayDetailLevel(entityDisplay), showEntityDescription: entityDisplay.description})}/>
          <Field label="Attribute display limit" hint="Counts eligible rows after display filters. Selected PK and business-key rows always remain visible and can exceed this total. Dictionaries and DDL always retain every attribute."><input aria-label="Attribute display limit" type="number" min={1} max={1000} value={draft.attributeDisplayLimit} disabled={draft.showAllAttributes} onChange={e=>{const value=Number(e.target.value);if(Number.isInteger(value)&&value>=1&&value<=1000)update({attributeDisplayLimit:value});}}/></Field>
          <label className="check-row"><input type="checkbox" checked={draft.showAllAttributes} onChange={e=>update({showAllAttributes:e.target.checked})}/>Show all attributes for canvas, printing and diagram export</label>
          {!!detailOverrideCount && <Button type="button" icon={RotateCcw} disabled={isLocked('diagramDetailLevels') || isLocked('diagramEntityDisplay')} onClick={() => update({diagramDetailLevels:{},diagramEntityDisplay:{}})}>Reset diagram detail overrides ({detailOverrideCount})</Button>}
          <Field label="Font family"><select disabled={isLocked("fontFamily")} value={draft.fontFamily} onChange={e => update({ fontFamily: e.target.value })}><option value="Inter, system-ui, sans-serif">System sans</option><option value="Arial, sans-serif">Arial</option><option value="Georgia, serif">Georgia</option><option value="'Courier New', monospace">Courier New</option></select></Field>
          <div className="form-row"><Field label={fieldLabel} hint={conceptual ? "Not applicable in a conceptual view. Your value remains available in logical and physical views." : "10-32 pixels; applies to the whole field row."}><FontSizeInput aria-label={fieldLabel} disabled={conceptual || isLocked("fontSize")} value={draft.fontSize} onChange={fontSize => update({ fontSize })} /></Field><Field label={titleLabel} hint="10-32 pixels at 100% zoom."><FontSizeInput aria-label={titleLabel} disabled={isLocked("titleSize")} value={draft.titleSize} onChange={titleSize => update({ titleSize })} /></Field></div>
          <div className="font-preview" style={{ fontFamily: draft.fontFamily, fontSize: draft.fontSize, color: tokens.text, background: tokens.surface, borderColor: tokens.border }}><strong style={{ fontSize: draft.titleSize, background: tokens.header }}>Customer</strong>{conceptual ? <span>Business identifier: Customer number</span> : <><span><KeyRound size={12} />customer_id<i>integer</i></span><span>customer_name<i>varchar</i></span></>}</div>
          <small className="subtle">Font size changes readability. Zoom changes the camera.</small>
          {([['centerEntityNames','Center names in name-only view'],['autoSizeEntities','Fit unlocked entities to text, growing or shrinking as needed'],['resizeOnDetailChange','Resize unlocked entity size when display detail changes (default: No)'],['entityPreviewEnabled','Show enlarged entity previews on hover']] as [keyof Preferences,string][]).map(([key,label]) => <label key={key} className="check-row"><input type="checkbox" checked={!!draft[key]} disabled={isLocked(key)} onChange={e => update({ [key]: e.target.checked })}/>{label}</label>)}
          <Field label="Preview hover delay" hint="A readable, scrollable preview opens after the pointer rests on an entity or table."><select value={draft.entityPreviewDelay} disabled={!draft.entityPreviewEnabled || isLocked("entityPreviewDelay")} onChange={e => update({ entityPreviewDelay: Number(e.target.value) })}>{[1,2,3,4,5].map(seconds => <option key={seconds} value={seconds * 1000}>{seconds} {seconds === 1 ? "second" : "seconds"}</option>)}</select></Field>
          <small className="subtle">Manual entity sizes stay locked. Use Unlock size in the entity menu to enable automatic sizing.</small>
        </section>
        <section className="a210-preference-section">
          <div className="a210-section-heading"><div><h3><GitFork size={18} />Relationships</h3><p>Personal defaults for lines, labels and creation.</p></div></div>
          <label className="check-row"><input type="checkbox" checked={draft.showRelationshipNames === false} disabled={isLocked("showRelationshipNames")} onChange={e => update({ showRelationshipNames: !e.target.checked })} />Hide relationship names</label>
          <fieldset aria-label="Automatic local layout adjustment"><legend>Automatic local layout adjustment</legend>
            <label className="check-row"><input type="checkbox" checked={autoAdjust} onChange={e => update({autoAdjustNearbyEntities:e.target.checked,autoAdjustRelationships:e.target.checked,preserveManualRoutes:!e.target.checked})}/>Enable local adjustment</label>
            <div style={{paddingInlineStart:18}}><label className="check-row"><input type="checkbox" checked={draft.autoAdjustNearbyEntities !== false} onChange={e => update({autoAdjustNearbyEntities:e.target.checked})}/>Move nearby entities to make space</label>
              <label className="check-row"><input type="checkbox" checked={draft.autoAdjustRelationships !== false} onChange={e => update({autoAdjustRelationships:e.target.checked,preserveManualRoutes:!e.target.checked})}/>Adjust nearby relationship routes</label></div>
            <small className="subtle">Pinned entities stay fixed. Turning adjustments off preserves valid arrangements; connections that cross entities still need the smallest safe repair. Changing this preference alone does not move saved objects.</small>
          </fieldset>
          <label className="check-row"><input type="checkbox" checked={draft.preserveManualRoutes || draft.autoAdjustRelationships === false} onChange={e => update({preserveManualRoutes:e.target.checked,autoAdjustRelationships:!e.target.checked})}/>Keep my manual relationship layout</label>
          <small className="subtle">Preserve valid bends when entities move. Applying an auto-layout preview can recompute routes.</small>
          <label className="check-row"><input type="checkbox" checked={draft.centerRelationshipEndpoints} onChange={e=>update({centerRelationshipEndpoints:e.target.checked})}/>Favor centered single-relationship endpoints</label>
          <label className="check-row"><input type="checkbox" checked={draft.avoidParentTopAttachment} onChange={e=>update({avoidParentTopAttachment:e.target.checked})}/>Use the parent's top edge as a fallback</label>
          <Field label="Parallel relationship gap" hint="Diagram pixels between nearby parallel lanes; dense constraints can require a compromise."><input aria-label="Parallel relationship gap" type="number" min={8} max={64} value={draft.relationshipLaneGap} onChange={e=>{const value=Number(e.target.value);if(Number.isInteger(value)&&value>=8&&value<=64)update({relationshipLaneGap:value});}}/></Field>
          <Field label="Relationship creation"><select aria-label="Relationship creation" value={draft.relationshipCreationMode} onChange={e => update({ relationshipCreationMode: e.target.value as Preferences['relationshipCreationMode'] })}><option value="endpoints">Select source and target first</option><option value="dialog">Open relationship dialog first</option></select></Field>
          <RelationshipStyleEditor value={draft.relationshipStyle} onChange={relationshipStyle => update({ relationshipStyle })} inherited={{ color: tokens.relationship }} disabled={isLocked("relationshipStyle")} />
          <RelationshipPreview value={draft.relationshipStyle} tokens={tokens} />
          <Button type="button" icon={RotateCcw} disabled={isLocked("relationshipStyle")} onClick={() => update({ relationshipStyle: structuredClone(defaultPreferences.relationshipStyle) })}>Reset relationship defaults</Button>
          <div className="a210-inline-help"><ContextHelp topic="relationship-appearance" onHelp={onHelp} label="About relationship labels" /></div>
        </section>
        <section className="a210-preference-section">
          <div className="a210-section-heading"><div><h3><Layers3 size={18} />User preferences</h3><p>Keep the controls and editing surfaces you use close at hand.</p></div></div>
          <label className="check-row"><input type="checkbox" checked={draft.allowUnlimitedCanvasExpansion} onChange={e=>update({allowUnlimitedCanvasExpansion:e.target.checked})}/>Allow unlimited canvas expansion</label>
          <small className="subtle">Edge dragging waits 0.5 seconds, pans up to 90 screen pixels/second and pauses 0.4 seconds after each second. Automatic zoom stops at 20%. Bounded mode permits a 2400-pixel margin around the starting diagram; Escape restores the complete gesture.</small>
          <Field label="Interface size" hint="50%-200% in 10% steps, independent of diagram zoom. This size is remembered for startup."><div className="form-row"><input aria-label="Interface size" type="range" min={50} max={200} step={10} value={Math.round(interfaceScaleForPreferences(draft)*100)} onChange={e => update({interfaceScale:normalizeInterfaceScale(Number(e.target.value)/100)})}/><output>{Math.round(interfaceScaleForPreferences(draft)*100)}%</output><Button type="button" onClick={() => update({interfaceScale:1})}>100%</Button></div></Field>
          <Button type="button" icon={draft.workspaceLayout.locked ? Unlock : Lock} aria-pressed={draft.workspaceLayout.locked} title={toolbarLockAction(draft.workspaceLayout)} onClick={() => update({workspaceLayout:{...draft.workspaceLayout,locked:!draft.workspaceLayout.locked}})}>{toolbarLockAction(draft.workspaceLayout)}</Button>
          <div className="a250-workspace-presets"><Button type="button" icon={Save} onClick={() => update({personalWorkspaceLayout:captureWorkspacePreset(draft.workspaceLayout)})}>Save current workspace as my default</Button>
            <Button type="button" icon={RotateCcw} disabled={!draft.personalWorkspaceLayout} onClick={() => draft.personalWorkspaceLayout && update({workspaceLayout:captureWorkspacePreset(draft.personalWorkspaceLayout)})}>Restore my workspace layout</Button>
            <Button type="button" icon={RotateCcw} onClick={() => update({workspaceLayout:builtInWorkspaceLayout()})}>Restore default workspace layout</Button></div>
          <small className="subtle">Your saved workspace includes palette positions, visibility, panel widths and locks. Further rearrangements do not overwrite it. Restoring built-in defaults keeps your saved preset. Changes save automatically.</small>
          <label className="check-row"><input type="checkbox" checked={draft.showAttributePropertiesOnSelection !== false} onChange={e => update({showAttributePropertiesOnSelection:e.target.checked})}/>Show attribute properties on selection</label>
          <Field label="Property presentation" hint="Double-click or Properties opens the same editor in your chosen surface."><select disabled={isLocked("propertyPresentation")} value={draft.propertyPresentation} onChange={e => update({ propertyPresentation: e.target.value as any })}><option value="popup">Popup dialog</option><option value="inspector">Side inspector</option></select></Field>
          <Field label="Auto-layout preview"><select aria-label="Auto-layout preview" value={draft.layoutPreviewMode} onChange={e => update({ layoutPreviewMode: e.target.value as Preferences['layoutPreviewMode'] })}><option value="single">Selected layout only</option><option value="all">All layout options</option></select></Field>
          <Field label="Layout controls"><select disabled={isLocked("layoutControls")} value={draft.layoutControls} onChange={e => update({ layoutControls: e.target.value as any })}><option value="icons">Icon palette</option><option value="compact">Compact menu</option></select></Field>
          <Field label="Layout"><select disabled={isLocked("workspace")} value={draft.workspace} onChange={e => update({ workspace: e.target.value as any })}><option value="balanced">Balanced · navigator and inspector</option><option value="focus">Canvas focus</option><option value="inspector-left">Inspector on the left</option></select></Field>
          <div className="form-row"><Field label="Settings presentation"><select disabled={isLocked("settingsView")} value={draft.settingsView} onChange={e => update({ settingsView: e.target.value as any })}><option value="icons">Icons</option><option value="list">Classic list</option></select></Field><Field label="Experience"><select disabled={isLocked("experience")} value={draft.experience} onChange={e => update({ experience: e.target.value as any })}><option value="guided">Guided</option><option value="standard">Standard</option><option value="expert">Expert</option></select></Field></div>
          {([ ["grid", "Show canvas grid"], ["snap", "Snap to grid"], ["railLabels", "Show navigation labels"] ] as const).map(([key, label]) => <label className="check-row" key={key}><input type="checkbox" disabled={isLocked(key)} checked={draft[key]} onChange={e => update({ [key]: e.target.checked })} />{label}</label>)}
        </section>
        <section className="a210-preference-section">
          <div className="a210-section-heading"><div><h3><ShieldCheck size={18} />Save & receive</h3><p>Choose when your validated work is saved and when shared changes arrive.</p></div></div>
          <Field label="Save my changes"><select value={draft.saveMode} onChange={e => update({ saveMode: e.target.value as any })}><option value="manual">Manual · save when ready</option><option value="automatic">Automatic · save validated commands</option></select></Field>
          <AutosaveIntervalInput value={draft.autosaveIntervalSeconds} onChange={autosaveIntervalSeconds => update({autosaveIntervalSeconds})}/>
          <label className="check-row"><input type="checkbox" checked={draft.showAutosaveSuccess} onChange={event => update({showAutosaveSuccess:event.target.checked})}/>Show non-blocking autosave success messages</label>
          <Field label="Receive others’ changes"><select value={draft.receiveMode} onChange={e => update({ receiveMode: e.target.value as any })}><option value="notify">Notify me</option><option value="automatic">Automatic · preserve dirty work</option></select></Field>
          <Notice>Model and preference saving are independent. Close keeps confirmed preferences; failed changes remain available to retry. Incomplete numeric input is applied only when valid.</Notice>
        </section>
      </div>
      <section className="a210-preference-section"><div className="a210-section-heading"><div><h3><Table2 size={18}/>Model-type presentation</h3><p>Separate defaults for Conceptual, Logical and Physical diagrams.</p></div></div>
        <ModelTypePresentationEditor value={draft.modelTypePresentation} preferences={draft} disabled={isLocked("modelTypePresentation")} lockedKeys={locked} onChange={modelTypePresentation => update({ modelTypePresentation })}/>
      </section>
      <section className="a210-preference-section"><ExportBackgroundEditor value={draft.exportBackgrounds} disabled={isLocked("exportBackgrounds")} onChange={exportBackgrounds => update({ exportBackgrounds })}/></section>
    </fieldset>
  </Modal>;
}
export function HistoryDialog({
  document,
  baseline,
  pendingCount,
  canPublish,
  onClose,
  onPublished,
}: {
  document: ModelDocument;
  baseline: ModelDocument;
  pendingCount: number;
  canPublish: boolean;
  onClose: () => void;
  onPublished: (p: any) => void;
}) {
  const [history, setHistory] = useState<any[]>([]);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("history");
  const [publication, setPublication] = useState<any>();
  const changes = useMemo(
    () => compareModels(baseline, document),
    [baseline, document],
  );
  useEffect(() => {
    api(`/models/${document.model.modelId}/history`)
      .then((r: any) => setHistory(Array.isArray(r) ? r : r.commits || []))
      .catch((e: any) => setError(errorText(e)));
  }, [document.model.modelId]);
  return (
    <Modal
      title="History & publication"
      subtitle="Saved revisions, local changes, and approved outputs are separate states."
      onClose={onClose}
      wide
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="tab-bar">
        {[
          ["history", "Revision history"],
          ["compare", `Draft changes (${changes.length})`],
          ["publish", "Publish a revision"],
        ].map(([id, label]) => (
          <button
            key={id}
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {tab === "history" && (
        <div className="history-list">
          {!history.length ? (
            <p className="subtle">No saved history available.</p>
          ) : (
            history.map((h, i) => (
              <div className="history-item" key={h.id || h.commitId || i}>
                <span className="history-marker" />
                <div>
                  <strong>{h.summary || "Model revision"}</strong>
                  <span>
                    Revision {h.revision} ·{" "}
                    {typeof h.actor === "string"
                      ? h.actor
                      : h.actor?.name || "Authorized actor"}
                  </span>
                  <small>
                    {new Date(h.createdAt || h.acceptedAt).toLocaleString()}
                  </small>
                  <code>{h.id || h.commitId}</code>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {tab === "compare" && (
        <>
          <Notice>
            This compares the current local draft with its saved baseline.
            Historical revision comparison is not available through this build’s
            public API.
          </Notice>
          {changes.length === 0 ? (
            <div className="validation-clean">
              <Check size={16} /> Draft and saved baseline are identical.
            </div>
          ) : (
            changes.map((c, i) => (
              <div className={`change-row ${c.kind}`} key={i}>
                <span className="tag">{c.kind}</span>
                <strong>{c.name}</strong>
                <p>{c.details}</p>
              </div>
            ))
          )}
        </>
      )}
      {tab === "publish" && (
        <>
          <Notice>
            Publication captures an immutable saved revision for permitted
            consumers. It does not deploy a database.
          </Notice>
          {pendingCount > 0 && (
            <Notice kind="warning">
              Save your {pendingCount} pending operations before publication.
            </Notice>
          )}
          <Field label="Publication name">
            <input
              placeholder="e.g. Architecture review · October"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Button
            variant="primary"
            disabled={pendingCount > 0 || !name.trim() || !canPublish}
            title={
              !canPublish ? "Requires model.publish permission" : undefined
            }
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                const p = await api(
                  `/models/${document.model.modelId}/publications`,
                  { method: "POST", body: JSON.stringify({ name }) },
                );
                setPublication(p);
                onPublished(p);
              } catch (e) {
                setError(errorText(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Publish saved revision
          </Button>
          {publication && (
            <div className="publication-result">
              <CheckCircle2 size={21} />
              <strong>Publication created</strong>
              <code>{publication.publicationId || publication.id}</code>
              <Button
                icon={ArrowDownToLine}
                onClick={() =>
                  downloadText(
                    `${name.replace(/[^a-z0-9_-]/gi, "_")}.publication.json`,
                    JSON.stringify(publication, null, 2),
                    "application/json",
                  )
                }
              >
                Download publication receipt
              </Button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

export function SelectionDialog({
  document,
  diagramId,
  selection,
  repositories,
  repositoryId,
  dirty,
  canCreate,
  sourceRevision,
  sourceCommitId,
  onClose,
  onOperations,
  onExtract,
}: {
  document: ModelDocument;
  diagramId: string;
  selection: string[];
  repositories: Repository[];
  repositoryId: string;
  dirty: boolean;
  canCreate: boolean;
  sourceRevision: number;
  sourceCommitId: string;
  onClose: () => void;
  onOperations: (ops: Operation[], summary: string) => void | boolean;
  onExtract: (data: any) => Promise<void>;
}) {
  const source = document.diagrams.find((d) => d.diagramId === diagramId);
  const roots = document.objects.filter(
    (o) => !o.ownerObjectId && o.representationId === source?.representationId,
  );
  const [mode, setMode] = useState("view");
  const [name, setName] = useState("");
  const [ids, setIds] = useState<string[]>(
    roots
      .filter(
        (o) =>
          selection.includes(o.objectId) ||
          document.objects.some(
            (c) =>
              c.ownerObjectId === o.objectId && selection.includes(c.objectId),
          ),
      )
      .map((o) => o.objectId),
  );
  const [repo, setRepo] = useState(
    repositoryId || repositories[0]?.repositoryId || "",
  );
  const [preview, setPreview] = useState<any>();
  const [error, setError] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setPreview(undefined);
    setAck(false);
  }, [document]);
  const inspect = () => {
    setError("");
    setAck(false);
    try {
      if (mode === "view") {
        const operations = createSubjectArea(document, name, ids, diagramId);
        setPreview({
          operations,
          diagnostics: [],
          lossReport: {
            preserved: [
              "Selected semantic object identities",
              "Existing fields, keys and relationship meaning",
            ],
            translated: ["New independent diagram placement"],
            preservedOpaque: [],
            lost: [],
            unresolved: [],
          },
        });
      } else {
        const result = extractModel(document, name, ids, diagramId);
        result.document.extensions.extraction = {
          sourceModelId: document.model.modelId,
          sourceRevision,
          sourceCommitId,
          relationship: "independent-copy",
        };
        setPreview(result);
      }
    } catch (e) {
      setError(errorText(e));
    }
  };
  const apply = async () => {
    if (mode === "view") {
      const accepted = onOperations(
        preview.operations,
        `Create subject view ${name}`,
      );
      if (accepted !== false) onClose();
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onExtract({
        name,
        repositoryId: repo,
        modelType: preview.document.model.modelType,
        family: preview.document.model.family,
        targetProfile: preview.document.model.targetProfile,
        initialDocument: preview.document,
      });
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const hasGaps = !!preview?.lossReport?.unresolved?.length;
  return (
    <Modal
      title="Create from a selection"
      subtitle="Choose whether to share the same objects or create independent identities."
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {preview ? (
            <Button
              variant="primary"
              busy={busy}
              disabled={
                preview.diagnostics?.some(
                  (d: Diagnostic) => d.severity === "error",
                ) ||
                (hasGaps && !ack) ||
                (mode === "copy" && (dirty || !canCreate))
              }
              onClick={() => void apply()}
            >
              {mode === "view"
                ? "Add subject view to draft"
                : "Create independent model"}
            </Button>
          ) : (
            <Button
              variant="primary"
              icon={Search}
              disabled={
                !ids.length ||
                !name.trim() ||
                (mode === "copy" && (dirty || !canCreate))
              }
              onClick={inspect}
            >
              Preview selection
            </Button>
          )}
        </>
      }
    >
      <div className="form-row">
        <Field label="Selection behavior">
          <select
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              setPreview(undefined);
            }}
          >
            <option value="view">
              Subject view · same objects, separate layout
            </option>
            <option value="copy" disabled={!canCreate}>
              Independent model · new identities and source provenance
            </option>
          </select>
        </Field>
        <Field label={mode === "view" ? "View name" : "New model name"}>
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setPreview(undefined);
            }}
            placeholder={
              mode === "view"
                ? "e.g. Customer identity"
                : "e.g. Customer subsystem"
            }
          />
        </Field>
      </div>
      {mode === "copy" ? (
        <>
          <Notice>
            Source objects remain unchanged. The copy has its own identities and
            will not synchronize automatically.
          </Notice>
          {dirty && (
            <Notice kind="warning">
              Save or discard local model and inspector changes before creating
              an independent copy. Extraction must refer to a saved source
              revision.
            </Notice>
          )}
          <Field label="Destination repository">
            <select value={repo} onChange={(e) => setRepo(e.target.value)}>
              {repositories.map((r) => (
                <option key={r.repositoryId} value={r.repositoryId}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
        </>
      ) : (
        <Notice>
          A subject view references the same entities and fields. Semantic
          changes are shared across views; layout is independent.
        </Notice>
      )}
      <div className="section-label">Entities in this diagram</div>
      <div className="selection-object-list">
        {roots.map((o) => (
          <label className="check-row" key={o.objectId}>
            <input
              type="checkbox"
              checked={ids.includes(o.objectId)}
              onChange={(e) => {
                setIds(
                  e.target.checked
                    ? [...ids, o.objectId]
                    : ids.filter((id) => id !== o.objectId),
                );
                setPreview(undefined);
              }}
            />
            <span>{o.name}</span>
            <small>
              {
                document.objects.filter((c) => c.ownerObjectId === o.objectId)
                  .length
              }{" "}
              owned fields / keys
            </small>
          </label>
        ))}
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {preview && (
        <>
          <div className="preview-counts">
            <span>{ids.length} selected root objects</span>
            <span>
              {mode === "view"
                ? "Stable object identities retained"
                : `${preview.document.objects.length} new object identities`}
            </span>
            <span>
              {mode === "view"
                ? "Independent layout"
                : `${preview.document.relationships.length} internal relationships`}
            </span>
          </div>
          <Diagnostics items={preview.diagnostics || []} />
          <LossSummary loss={preview.lossReport} />
          {hasGaps && (
            <label className="check-row">
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
              />
              I reviewed the unresolved external relationships; they remain
              explicit dependencies in the copy.
            </label>
          )}
        </>
      )}
    </Modal>
  );
}
